// Route adapter: binds the URL's search params to the deals screen's state.
// Deliberately the only part of this screen that knows a router exists — the
// list and the board each take state and an onChange, which is what makes them
// testable without a route.
//
// The URL carries the QUERY (search, filters, sort, page), the VIEW showing it
// (list, board or changes), and the changes view's own period and measure.
// They are read and written separately so a filter change cannot lose the view
// and a view change cannot lose the filters.
//
// The title and subtitle are DECLARED, not rendered: the shell owns the top bar
// (see `pageChrome.tsx`), so a heading drawn here would be a second one.

import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import { todayIsoDate } from "../../format/calendarDate.js";
import { useHasRole } from "../../auth/useAuth.js";
import { usePageChrome } from "../../design/pageChrome.js";
import type { DealsQueryState } from "../../api/queries.js";
import { DealFormDialog } from "./DealFormDialog.js";
import { DealsBoard } from "./DealsBoard.js";
import { DealsList } from "./DealsList.js";
import { DealsChanges } from "./DealsChanges.js";
import {
  boardMeasureToSearch,
  changesStateToSearch,
  queryStateToSearch,
  searchToBoardMeasure,
  searchToChangesState,
  searchToQueryState,
  searchToViewMode,
  viewModeToSearch,
  type ChangesState,
  type DealsViewMode,
} from "./dealsSearch.js";
import type { BoardMeasure } from "./boardModel.js";

export function DealsPage(): ReactNode {
  const search = useSearch({ from: "/deals" });
  const navigate = useNavigate();
  const state = searchToQueryState(search);
  const view = searchToViewMode(search);
  const measure = searchToBoardMeasure(search);
  const today = todayIsoDate();
  const changes = searchToChangesState(search, today);
  const canEdit = useHasRole("crm_editor");
  const [creating, setCreating] = useState(false);

  usePageChrome({ title: "Deals" });

  const go = (
    nextState: DealsQueryState,
    nextView: DealsViewMode,
    nextMeasure: BoardMeasure,
    nextChanges: ChangesState = changes,
  ): void => {
    void navigate({
      to: "/deals",
      search: {
        ...queryStateToSearch(nextState),
        ...viewModeToSearch(nextView),
        ...boardMeasureToSearch(nextMeasure),
        ...changesStateToSearch(nextChanges, today),
      },
      replace: true,
    });
  };

  const shared = {
    state,
    onStateChange: (next: DealsQueryState) => go(next, view, measure),
    view,
    // The board has no page, so switching to it starts the list over at the
    // first one — a `?page=3` carried through a board would be a page of
    // nothing on the way back.
    onViewChange: (next: DealsViewMode) => go({ ...state, page: 1 }, next, measure),
    ...(canEdit ? { onCreate: () => setCreating(true) } : {}),
  };

  return (
    <>
      {view === "list" ? (
        <DealsList {...shared} />
      ) : view === "changes" ? (
        <DealsChanges
          {...shared}
          today={today}
          changes={changes}
          onChangesChange={(next) => go(state, view, measure, next)}
        />
      ) : (
        <DealsBoard
          {...shared}
          measure={measure}
          onMeasureChange={(next) => go(state, view, next)}
        />
      )}

      {creating && (
        <DealFormDialog
          onClose={() => setCreating(false)}
          // Straight to the new deal: a deal isn't finished until it has revenue
          // lines, and those live on its detail page.
          onSaved={(deal) => void navigate({ to: "/deals/$dealId", params: { dealId: deal.id } })}
        />
      )}
    </>
  );
}
